import React, { useRef, useState } from 'react';
import { submitContactForm } from '../../utils/submitContactForm';
import './contactForm.css';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const DEFAULT_CLASS_NAMES = {
  form: 'cf-form',
  row: 'cf-row',
  field: 'cf-field',
  fieldMessage: 'cf-field cf-field--message',
  input: 'cf-input',
  inputError: 'cf-input--error',
  textarea: 'cf-textarea',
  textareaError: 'cf-textarea--error',
  error: 'cf-error',
  button: 'cf-btn',
  status: 'cf-status',
  statusSuccess: 'cf-status--success',
  statusError: 'cf-status--error',
};

export const CONTACT_FORM_CLASSES = {
  home: {
    form: 'hp-contact__form',
    row: 'hp-contact__form-row',
    field: 'hp-contact__field',
    fieldMessage: 'hp-contact__field hp-contact__field--message',
    input: 'hp-contact__input',
    inputError: 'hp-contact__input--error',
    textarea: 'hp-contact__textarea',
    textareaError: 'hp-contact__textarea--error',
    error: 'hp-contact__field-error',
    button: 'hp-contact__btn',
    status: 'hp-contact__form-msg',
    statusSuccess: 'hp-contact__form-msg--success',
    statusError: 'hp-contact__form-msg--error',
  },
  contactPage: {
    form: 'cn-form',
    row: 'cn-form-row',
    field: 'cn-field',
    fieldMessage: 'cn-field cn-field--message',
    input: 'cn-input',
    inputError: 'cn-input--error',
    textarea: 'cn-input cn-input--textarea',
    textareaError: 'cn-input--error',
    error: 'cn-field-error',
    button: 'cn-submit-btn',
    status: 'cn-form-msg',
    statusSuccess: 'cn-form-msg--success',
    statusError: 'cn-form-msg--error',
  },
  bloodTesting: {
    form: 'bt-contact__form',
    row: 'bt-contact__row',
    field: 'bt-contact__field',
    fieldMessage: 'bt-contact__field bt-contact__field--message',
    input: 'bt-contact__input',
    inputError: 'bt-contact__input--error',
    textarea: 'bt-contact__textarea bt-contact__textarea--full',
    textareaError: 'bt-contact__textarea--error',
    error: 'bt-contact__field-error',
    button: 'bt-contact__btn',
    status: 'bt-contact__form-msg',
    statusSuccess: 'bt-contact__form-msg--success',
    statusError: 'bt-contact__form-msg--error',
  },
  travelClinic: {
    form: 'tc-contact__form',
    row: 'tc-contact__row',
    field: 'tc-contact__field',
    fieldMessage: 'tc-contact__field tc-contact__field--message',
    input: 'tc-contact__input',
    inputError: 'tc-contact__input--error',
    textarea: 'tc-contact__textarea',
    textareaError: 'tc-contact__textarea--error',
    error: 'tc-contact__field-error',
    button: 'tc-contact__submit',
    status: 'tc-contact__form-msg',
    statusSuccess: 'tc-contact__form-msg--success',
    statusError: 'tc-contact__form-msg--error',
  },
};

export const validateContactField = (name, value) => {
  const trimmed = value.trim();

  if (name === 'name') {
    if (!trimmed) return 'Please enter your name';
    if (trimmed.length < 2) return 'Name must be at least 2 characters';
    return '';
  }

  if (name === 'email') {
    if (!trimmed) return 'Please enter your email';
    if (!EMAIL_REGEX.test(trimmed)) return 'Please enter a valid email address';
    return '';
  }

  if (name === 'message') {
    if (!trimmed) return 'Please enter your message';
    return '';
  }

  return '';
};

const validateForm = (form) => {
  const nextErrors = {};
  ['name', 'email', 'message'].forEach((field) => {
    const error = validateContactField(field, form[field]);
    if (error) nextErrors[field] = error;
  });
  return nextErrors;
};

const withErrorClass = (base, errorClass, hasError) =>
  hasError ? `${base} ${errorClass}` : base;

const ContactForm = ({
  classNames = {},
  idPrefix = 'contact',
  variant,
  submitLabel = 'SEND MESSAGE',
  sendingLabel = 'SENDING…',
  textareaRows,
  className,
}) => {
  const classes = { ...DEFAULT_CLASS_NAMES, ...classNames };
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const fieldRefs = {
    name: useRef(null),
    email: useRef(null),
    message: useRef(null),
  };

  const updateFieldError = (name, value) => {
    setErrors((prev) => {
      const fieldError = validateContactField(name, value);
      if (!fieldError) {
        if (!prev[name]) return prev;
        const next = { ...prev };
        delete next[name];
        return next;
      }
      return { ...prev, [name]: fieldError };
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setStatus((current) => (current === 'success' || current === 'error' ? 'idle' : current));
    if (errors[name]) updateFieldError(name, value);
  };

  const handleBlur = (e) => {
    updateFieldError(e.target.name, e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = validateForm(form);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      const firstField = ['name', 'email', 'message'].find((field) => nextErrors[field]);
      fieldRefs[firstField]?.current?.focus();
      return;
    }

    setStatus('sending');
    try {
      await submitContactForm(form);
      setStatus('success');
      setForm({ name: '', email: '', message: '' });
      setErrors({});
    } catch {
      setStatus('error');
    }
  };

  const formClass = [
    classes.form,
    variant === 'dark' ? 'cf-form--dark' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <form className={formClass} onSubmit={handleSubmit} noValidate>
      <div className={classes.row}>
        <div className={classes.field}>
          <input
            ref={fieldRefs.name}
            name="name"
            type="text"
            className={withErrorClass(classes.input, classes.inputError, Boolean(errors.name))}
            placeholder="Your Name*"
            value={form.name}
            onChange={handleChange}
            onBlur={handleBlur}
            aria-label="Your name"
            aria-invalid={Boolean(errors.name)}
            aria-describedby={errors.name ? `${idPrefix}-name-error` : undefined}
          />
          {errors.name && (
            <p id={`${idPrefix}-name-error`} className={classes.error} role="alert">
              {errors.name}
            </p>
          )}
        </div>
        <div className={classes.field}>
          <input
            ref={fieldRefs.email}
            name="email"
            type="email"
            className={withErrorClass(classes.input, classes.inputError, Boolean(errors.email))}
            placeholder="Your Email*"
            value={form.email}
            onChange={handleChange}
            onBlur={handleBlur}
            aria-label="Your email address"
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? `${idPrefix}-email-error` : undefined}
          />
          {errors.email && (
            <p id={`${idPrefix}-email-error`} className={classes.error} role="alert">
              {errors.email}
            </p>
          )}
        </div>
      </div>
      <div className={classes.fieldMessage}>
        <textarea
          ref={fieldRefs.message}
          name="message"
          className={withErrorClass(classes.textarea, classes.textareaError, Boolean(errors.message))}
          placeholder="Your Message*"
          rows={textareaRows}
          value={form.message}
          onChange={handleChange}
          onBlur={handleBlur}
          aria-label="Your message"
          aria-invalid={Boolean(errors.message)}
          aria-describedby={errors.message ? `${idPrefix}-message-error` : undefined}
        />
        {errors.message && (
          <p id={`${idPrefix}-message-error`} className={classes.error} role="alert">
            {errors.message}
          </p>
        )}
      </div>
      <button type="submit" className={classes.button} disabled={status === 'sending'}>
        {status === 'sending' ? sendingLabel : submitLabel}
      </button>
      {status === 'success' && (
        <p className={`${classes.status} ${classes.statusSuccess}`} role="status">
          Email sent successfully!
        </p>
      )}
      {status === 'error' && (
        <p className={`${classes.status} ${classes.statusError}`} role="alert">
          Failed to send email. Please try again.
        </p>
      )}
    </form>
  );
};

export default ContactForm;
